import { MaterialCommunityIcons } from '@expo/vector-icons';
import {
  FIND_ASSIGNABLE_GROUPS,
  Group,
  SortDirectionEnum,
  cacheKeys,
  useCustomMutation,
} from '@gripp/shared-logic';
import {
  AssignmentListGrid,
  BaseColumnCheckbox,
  Colors,
  tokens,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { AssignmentModal } from '../assignment/assignmentModal';
import { ModalButton } from '../modalButton';

type GroupAssignmentColumnProps = {
  entityId: string;
  entityName: string;
  currentGroups: Group[];
  workspaceId: string;
  mutation: string;
  buildInput: (
    entityId: string,
    workspaceId: string,
    added: { id: string }[],
    removed: { id: string }[]
  ) => { input: Record<string, unknown> };
  cacheKey: string;
};

export const GroupAssignmentColumn = ({
  entityId,
  entityName,
  currentGroups,
  workspaceId,
  mutation,
  buildInput,
  cacheKey,
}: GroupAssignmentColumnProps) => {
  const groupCount = currentGroups?.length ?? 0;

  return (
    <ModalButton
      label=""
      customButton={
        <View style={styles.iconContainer}>
          <MaterialCommunityIcons
            name="account-group-outline"
            color={Colors.role.textPrimary}
            size={16}
            style={styles.icon}
          />
          <Text style={styles.countText}>{groupCount}</Text>
        </View>
      }
      modalComponent={({ onComplete }) => (
        <GroupAssignmentForm
          entityId={entityId}
          entityName={entityName}
          currentGroups={currentGroups}
          workspaceId={workspaceId}
          mutation={mutation}
          buildInput={buildInput}
          cacheKey={cacheKey}
          onComplete={onComplete}
        />
      )}
      containerStyle={styles.modalContainer}
    />
  );
};

type GroupAssignmentFormProps = {
  entityId: string;
  entityName: string;
  currentGroups: Group[];
  workspaceId: string;
  mutation: string;
  buildInput: (
    entityId: string,
    workspaceId: string,
    added: { id: string }[],
    removed: { id: string }[]
  ) => { input: Record<string, unknown> };
  cacheKey: string;
  onComplete: () => void;
};

const GroupAssignmentForm = ({
  entityId,
  entityName,
  currentGroups,
  workspaceId,
  mutation,
  buildInput,
  cacheKey,
  onComplete,
}: GroupAssignmentFormProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const initialGroupIds = useMemo(
    () => (currentGroups ?? []).filter((g) => g?.id).map((g) => g.id),
    [currentGroups]
  );

  const { mutateAsync: updateGroups } = useCustomMutation({ query: mutation });

  return (
    <AssignmentModal
      title={entityName}
      showTabs={false}
      onComplete={onComplete}
      tabs={[
        {
          key: 'groups',
          label: '',
          searchPlaceholder: t('groups.assignments.groupSearchPlaceholder'),
          assignments: initialGroupIds,
          onSave: async ({ added, removed }) => {
            await updateGroups(
              buildInput(
                entityId,
                workspaceId,
                added.map((id) => ({ id })),
                removed.map((id) => ({ id }))
              ),
              {
                onSettled: async () => {
                  await queryClient.invalidateQueries({
                    queryKey: cacheKeys(cacheKey).all,
                  });
                },
              }
            );
          },
          renderList: (p) => (
            <GroupAssignmentList
              workspaceId={workspaceId}
              maxHeight={p.maxHeight}
              keyword={p.keyword}
              assignments={p.assignments}
              updateAssignments={p.onAssignmentsChange}
            />
          ),
        },
      ]}
    />
  );
};

type GroupAssignmentListProps = {
  workspaceId: string;
  maxHeight?: number;
  keyword?: string;
  assignments: string[];
  updateAssignments: (assignments: string[]) => void;
};

const useGroupAssignmentColumns = () => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper();

  const columns: ColumnDef<unknown, never>[] = [
    columnHelper.accessor('selected', {
      cell: BaseColumnCheckbox.checkbox,
      header: BaseColumnCheckbox.checkboxHeader,
      meta: { style: listStyles.checkboxCell },
    }),
    columnHelper.accessor('name', {
      cell: (info) => (
        <Text style={[listStyles.nameLabel, listStyles.cellText]}>
          {info.getValue() as string}
        </Text>
      ),
      header: () => t('groups.assignments.name'),
      meta: { style: listStyles.cell },
    }),
  ];

  return columns;
};

const GroupAssignmentList = ({
  workspaceId,
  maxHeight,
  keyword,
  assignments,
  updateAssignments,
}: GroupAssignmentListProps) => {
  const { t } = useTranslation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const columns = useGroupAssignmentColumns() as ColumnDef<any, any>[];

  const filter = useMemo(
    () => ({ workspaceId, assignments, keyword }),
    // `assignments` intentionally excluded: id-selection keeps the picked rows
    // regardless of the filter, so re-fetching on every toggle is unnecessary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspaceId, keyword]
  );

  return (
    <AssignmentListGrid
      modelName="group"
      query={FIND_ASSIGNABLE_GROUPS}
      dataPrefix="group.findAssignableGroups"
      columns={columns}
      filter={filter}
      assignments={assignments}
      onAssignmentsChange={updateAssignments}
      maxHeight={maxHeight}
      textEmpty={t('groups.common.textEmpty')}
      orderBy={{ field: 'name', order: SortDirectionEnum.Asc }}
    />
  );
};

const styles = StyleSheet.create({
  iconContainer: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  icon: {
    marginRight: tokens.spacing.xxs,
  },
  countText: {
    fontSize: 16,
    lineHeight: 17,
    fontWeight: '500',
    color: Colors.role.textStrong,
  },
  modalContainer: {
    width: 640,
    height: '80%',
    minHeight: 450,
    alignSelf: 'center',
    backgroundColor: Colors.role.surfaceHover,
    borderRadius: 10,
  },
});

const listStyles = StyleSheet.create({
  cell: {
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
    paddingVertical: tokens.spacing.xs,
    ...Platform.select({
      web: {
        height: 50,
        paddingRight: 16,
      },
      default: {
        height: 68,
        paddingRight: 12,
      },
    }),
  },
  checkboxCell: {
    maxWidth: 50,
    flexDirection: 'row',
    justifyContent: 'center',
  },
  nameLabel: {
    color: Colors.role.textStrong,
  },
  cellText: {
    fontSize: 16,
    lineHeight: 17,
    ...Platform.select({
      web: {
        fontWeight: '500',
        flexWrap: 'nowrap',
      },
      default: {
        fontWeight: '400',
        flexWrap: 'wrap',
      },
    }),
    color: Colors.role.textStrong,
  },
});
